import * as React from "react"
import {Map} from "immutable"

import {Demand as DemandModel, DemandList, fromJs as demandFromJs, JsonData as DemandJsonData} from "./../model/Demand"
import {Division, Divisions, fromJs as divisionFromJs, JsonData as DivisionJsonData} from "./../model/Division"
import {JsonData as UniversityBranchJsonData} from "./../model/UniversityBranch"
import {Speciality, Specialities, fromJs as specialityFromJs, JsonData as SpecialityJsonData} from "./../model/Speciality"
import {
    DEMAND_YEARS,
    EMPLOYEE_YEAR,
    ENTRANCE_YEARS,
    VACANCY_YEARS
} from "../Demand/common";
import {logger} from "../utils/logger";
import {Employee, Fired} from "../model/Employee";
import {Preloader} from "../React/Preloader";

interface PartialState {
    loaded?: boolean
    demand?: DemandList
    divisions?: Divisions
    specialities?: Specialities
}

interface State extends PartialState {
    loaded: boolean
}

export const yearColumns = VACANCY_YEARS.concat(DEMAND_YEARS);
export const columns = ["employee21", "employee21Avg", ...yearColumns]
export const entranceColumns = ENTRANCE_YEARS;

export type SummaryRow = {
    employee21: number
    employee21Avg: number
    2016: number
    2017: number
    2018: number
    2019: number
    2020: number
    2021: number
    2022: number
    2023: number
    2024: number
    2025: number
    2026: number
    2027: number
    2028: number
    2029: number
    2030: number
}

type SummaryMatrix = {
    demandNuclear: SummaryRow
    demandTotal: SummaryRow
    targetRequestNuclear: SummaryRow
    targetRequestTotal: SummaryRow
}

type SummaryMatrixTotal = {
    company: {[key: number]: SummaryMatrix}
    division: {[key: number]: SummaryMatrix}
    total: SummaryMatrix
}

type Response = {
    demand: DemandJsonData[]
    universityBranches: UniversityBranchJsonData[]
    divisions: DivisionJsonData[]
    specialities: SpecialityJsonData[]
}


export class FinalPrevious extends React.PureComponent<{}, PartialState> {
    state: State = {
        loaded: false,
    }

    componentDidMount() {
        fetch("/export/prior", {credentials: "include"})
            //     fetch("http://127.0.0.1:8080/js/325.json")
            .then(response => response.json())
            .then<Response>(response => response as any)
            .then(({demand, divisions, specialities: rawSpecialities}: Response) => {
                const specialities: Specialities = Map(rawSpecialities.map(speciality => [speciality.id, specialityFromJs(speciality)]));

                this.setState({
                    loaded: true,
                    demand:             Map(demand.map(demand => [demand.id, demandFromJs(demand, specialities)])) as DemandList,
                    divisions:          Map(divisions.map(division => [division.id, divisionFromJs(division)])) as Divisions,
                    specialities,
                })
            })
            .catch(error => logger.captureException(error))
    }

    render() {
        if (!this.state.loaded) {
            return <Preloader/>
        }

        const {
            demand,
            specialities,
            divisions,
        } = this.state

        try {
            if (specialities === undefined) {
                throw new Error("Specialities are empty for Preloader.tsx. Possibly rendering before fetching data")
            }

            if (demand === undefined) {
                throw new Error("Demand is empty for Preloader.tsx. Possibly rendering before fetching data")
            }

            if (divisions === undefined) {
                throw new Error("Divisions are empty for Preloader.tsx. Possibly rendering before fetching data")
            }
        } catch (e) {
            logger.captureException(e)

            return <span/>
        }


        let summary: SummaryMatrixTotal = {
            company: {},
            division: {},
            total: {} as SummaryMatrix,
        }

        ;(["demandNuclear", "demandTotal", "targetRequestNuclear", "targetRequestTotal"]).map(row => {
            summary.total[row] = {}
            columns.map(column => (summary.total[row][column] = 0))
        })

        demand.map(({
            specialityId,
            employees,
            demandByYear,
            targetRequestByYear,
            addBachelorTarget,
            companyId,
        }: DemandModel) => {
            const speciality: Speciality = specialities.get(specialityId)

            if (speciality) {
                const divisionId = divisions.find((division: Division) => division.companies.has(companyId)).id

                if (!summary.company[companyId]) {
                    summary.company[companyId] = {} as SummaryMatrix

                    ;(["demandNuclear", "demandTotal", "targetRequestNuclear", "targetRequestTotal"]).map(row => {
                        summary.company[companyId][row] = {}
                        columns.map(column => (summary.company[companyId][row][column] = 0))
                    })
                }

                if (!summary.division[divisionId]) {
                    summary.division[divisionId] = {} as SummaryMatrix

                    ;(["demandNuclear", "demandTotal", "targetRequestNuclear", "targetRequestTotal"]).map(row => {
                        summary.division[divisionId][row] = {}
                        columns.map(column => (summary.division[divisionId][row][column] = 0))
                    })
                }

                const employeesFiltered = employees.filter((employee: Employee) => employee.demandYear === EMPLOYEE_YEAR && employee.fired !== Fired.Fired);

                if (employeesFiltered.size) {
                    summary.company[companyId].demandTotal.employee21 += employeesFiltered.size
                    summary.company[companyId].demandTotal.employee21Avg += employeesFiltered.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                    summary.division[divisionId].demandTotal.employee21 += employeesFiltered.size
                    summary.division[divisionId].demandTotal.employee21Avg += employeesFiltered.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                    summary.total.demandTotal.employee21 += employeesFiltered.size
                    summary.total.demandTotal.employee21Avg += employeesFiltered.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                }

                yearColumns.map(year => {
                    summary.company[companyId].demandTotal[year] += (demandByYear[year] || 0)
                    summary.division[divisionId].demandTotal[year] += (demandByYear[year] || 0)
                    summary.total.demandTotal[year] += (demandByYear[year] || 0)
                })

                entranceColumns.map(year => {
                    summary.company[companyId].targetRequestTotal[year] += (targetRequestByYear[year] || 0)
                    summary.division[divisionId].targetRequestTotal[year] += (targetRequestByYear[year] || 0)
                    summary.total.targetRequestTotal[year] += (targetRequestByYear[year] || 0)

                    if (addBachelorTarget && speciality.bachelorSpecialityId) {
                        summary.company[companyId].targetRequestTotal[year] += (targetRequestByYear[year + 4] || 0)
                        summary.division[divisionId].targetRequestTotal[year] += (targetRequestByYear[year + 4] || 0)
                        summary.total.targetRequestTotal[year] += (targetRequestByYear[year + 4] || 0)
                    }
                })

                if (speciality.isNuclear()) {
                    yearColumns.forEach(year => {
                        summary.company[companyId].demandNuclear[year] += (demandByYear[year] || 0)
                        summary.division[divisionId].demandNuclear[year] += (demandByYear[year] || 0)
                        summary.total.demandNuclear[year] += (demandByYear[year] || 0)
                    })

                    entranceColumns.map(year => {
                        summary.company[companyId].targetRequestNuclear[year] += (targetRequestByYear[year] || 0)
                        summary.division[divisionId].targetRequestNuclear[year] += (targetRequestByYear[year] || 0)
                        summary.total.targetRequestNuclear[year] += (targetRequestByYear[year] || 0)

                        if (addBachelorTarget && speciality.bachelorSpecialityId) {
                            summary.company[companyId].targetRequestNuclear[year] += (targetRequestByYear[year + 4] || 0)
                            summary.division[divisionId].targetRequestNuclear[year] += (targetRequestByYear[year + 4] || 0)
                            summary.total.targetRequestNuclear[year] += (targetRequestByYear[year + 4] || 0)
                        }
                    })

                    if (employeesFiltered.size) {
                        summary.company[companyId].demandNuclear.employee21 += employeesFiltered.size
                        summary.company[companyId].demandNuclear.employee21Avg += employeesFiltered.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                        summary.division[divisionId].demandNuclear.employee21 += employeesFiltered.size
                        summary.division[divisionId].demandNuclear.employee21Avg += employeesFiltered.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                        summary.total.demandNuclear.employee21 += employeesFiltered.size
                        summary.total.demandNuclear.employee21Avg += employeesFiltered.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                    }
                }
            }
        })

        console.log(summary)
        return <pre>{JSON.stringify(summary, undefined, 2)}</pre>
    }
}